/**
 * Per-leg SLA presentation, shared by the job editor, the job detail page and the
 * job list.
 *
 * WHY SHARED: the same numbers used to be formatted by hand in three places, and
 * hand-written copies of one rule is exactly how the per-leg SLA cell ended up
 * disagreeing with itself across two pages (see the header of SlaCell.tsx).
 *
 * THE RULE these helpers encode, which the UI must not contradict:
 *
 *   - The two per-leg thresholds are the ONLY SLA controls. The former job-wide
 *     `sla_minutes` fallback is retired; nothing stands behind a blank leg.
 *   - A leg with no threshold is NOT EVALUATED. That is not a pass, and it is not
 *     "pending" either — no verdict will ever arrive for it.
 *   - A run passes only when EVERY configured leg passes. A leg breach therefore
 *     fails the run even when the total elapsed time fits inside the sum of the
 *     legs: 6 outbound + 3 inbound breaches a 5/5 configuration despite 9 < 10.
 *   - The overall window is DERIVED, never configured: it is the sum of the
 *     configured legs. With 5 and 5 the overall is 10 by definition. It is not a
 *     separate threshold and produces no verdict of its own.
 */

export interface LegThresholds {
  outbound_sla_minutes?: number | null;
  inbound_sla_minutes?: number | null;
}

/** How a blank leg is named everywhere in the UI. Deliberately not "no SLA". */
export const NOT_EVALUATED = 'not evaluated';

/**
 * One leg's threshold for display. `0` is a real threshold ("must be instantaneous"),
 * so this tests the TYPE rather than truthiness — `!minutes` would print 0 as
 * "not evaluated", inverting the strictest setting into no setting at all.
 */
export function legThresholdText(minutes: number | null | undefined): string {
  return typeof minutes === 'number' ? `${minutes} min` : NOT_EVALUATED;
}

/**
 * The derived overall window, or null when a leg is not evaluated — an unevaluated
 * leg is unbounded, so no overall figure exists to state.
 */
export function derivedOverallMinutes(t: LegThresholds): number | null {
  const out = t.outbound_sla_minutes;
  const inb = t.inbound_sla_minutes;
  if (typeof out !== 'number' || typeof inb !== 'number') return null;
  return out + inb;
}

/** Plain-language overall window, for a form hint or a summary row. */
export function overallWindowText(t: LegThresholds): string {
  const out = t.outbound_sla_minutes;
  const inb = t.inbound_sla_minutes;
  const total = derivedOverallMinutes(t);
  if (total !== null) return `${total} min (${out} outbound + ${inb} inbound)`;
  if (typeof out === 'number') return `unbounded — the inbound leg is ${NOT_EVALUATED}`;
  if (typeof inb === 'number') return `unbounded — the outbound leg is ${NOT_EVALUATED}`;
  return `unbounded — neither leg is evaluated`;
}

/** Compact both-legs summary for a table cell. */
export function legSummaryText(t: LegThresholds): string {
  return `out ${legThresholdText(t.outbound_sla_minutes)} · in ${legThresholdText(t.inbound_sla_minutes)}`;
}
